import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  Pagination,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import { toast } from "react-toastify";
import api from "../../api";
import HtmlWithLatex from "../QuestionBank/components/HtmlWithLatex";
import QuestionBankQuestionEditDialog from "../QuestionBank/components/QuestionBankQuestionEditDialog";
import QuestionVerifyDialog from "../../components/QuestionVerifyDialog";
import VideoVerificationPanel from "../ExplanationVideos/VideoVerificationPanel";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

function flattenExamCategories(nodes, depth = 0) {
  return (Array.isArray(nodes) ? nodes : []).flatMap((n) => [
    { id: n.id, name: `${"— ".repeat(depth)}${n.name}` },
    ...flattenExamCategories(n.all_children ?? n.allChildren ?? n.children ?? [], depth + 1),
  ]);
}

function getCurrentUserId() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}")?.id ?? null;
  } catch {
    return null;
  }
}

const STATUS_CONFIG = {
  approved: { label: "Approved", color: "success", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  rejected: { label: "Rejected", color: "error",   icon: <CancelOutlinedIcon fontSize="small" /> },
  flagged:  { label: "Flagged",  color: "warning",  icon: <CancelOutlinedIcon fontSize="small" /> },
};

const FIELD_LABELS = {
  question_text: "Question Text",
  explanation: "Explanation",
  default_marks: "Default Marks",
  difficulty: "Difficulty",
  options: "Options",
  sub_questions: "Sub-questions",
  image_url: "Image",
  image_urls: "Images",
  correct_answer: "Correct Answer",
  correct_answer_text: "Answer Text",
  correct_blanks: "Blank Answers",
  section_instructions: "Section Instructions",
  type: "Question Type",
};

const fieldLabel = (key) => FIELD_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const stripHtml = (s) => s?.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").trim() ?? "";

const PLATFORM_COLORS = { web: "info", mobile: "secondary" };

function difficultyChipColor(difficulty) {
  const key = String(difficulty).toLowerCase();
  if (key === "easy") return "success";
  if (key === "hard") return "error";
  return "warning";
}

function PlatformChip({ platform }) {
  if (!platform) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return <Chip label={platform} size="small" variant="outlined" color={PLATFORM_COLORS[platform] || "default"} sx={{ height: 20, fontSize: "0.65rem" }} />;
}

function PlatformStatusChip({ platform, approved, rejected }) {
  const total = (approved ?? 0) + (rejected ?? 0);
  if (total === 0) {
    return <Chip label={`${platform} —`} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />;
  }
  if ((rejected ?? 0) > 0) {
    return <Chip label={`${platform} ✗`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />;
  }
  return <Chip label={`${platform} ✓`} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />;
}

function PlatformCoverageChips({ item }) {
  const webApproved = item.web_approved ?? 0;
  const webRejected = item.web_rejected ?? 0;
  const mobileApproved = item.mobile_approved ?? 0;
  const mobileRejected = item.mobile_rejected ?? 0;
  const webTotal = webApproved + webRejected;
  const mobileTotal = mobileApproved + mobileRejected;
  const showMobileGap = webApproved > 0 && mobileTotal === 0;

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        <PlatformStatusChip platform="Web" approved={webApproved} rejected={webRejected} />
        <PlatformStatusChip platform="Mobile" approved={mobileApproved} rejected={mobileRejected} />
      </Stack>
      {showMobileGap && (
        <Chip label="Mobile not verified" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.6rem", alignSelf: "flex-start" }} />
      )}
    </Stack>
  );
}

function VerdictChip({ verdict }) {
  if (!verdict) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const cfg = STATUS_CONFIG[verdict] ?? { label: verdict, color: "default", icon: null };
  return <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" />;
}

function groupVerificationsByTester(verifications = []) {
  const map = new Map();
  verifications.forEach((v) => {
    const key = v.user?.id ?? v.user?.name ?? "unknown";
    if (!map.has(key)) {
      map.set(key, { name: v.user?.name ?? "—", web: null, mobile: null, notes: [] });
    }
    const row = map.get(key);
    const platform = v.platform || "web";
    row[platform] = v;
    if (v.notes) row.notes.push(v.notes);
  });
  return Array.from(map.values());
}

function matchesCoverageFilter(item, filterCoverage) {
  if (!filterCoverage) return true;

  const webApproved = item.web_approved ?? 0;
  const webRejected = item.web_rejected ?? 0;
  const mobileApproved = item.mobile_approved ?? 0;
  const mobileRejected = item.mobile_rejected ?? 0;
  const webTotal = webApproved + webRejected;
  const mobileTotal = mobileApproved + mobileRejected;

  switch (filterCoverage) {
    case "both":
      return webApproved > 0 && mobileApproved > 0;
    case "web_only":
      return webApproved > 0 && mobileTotal === 0;
    case "mobile_only":
      return mobileTotal > 0 && webTotal === 0;
    case "none":
      return webApproved === 0 && mobileApproved === 0;
    case "has_rejected":
      return webRejected > 0 || mobileRejected > 0;
    default:
      return true;
  }
}

function matchesVerdictFilter(item, filterVerdict) {
  if (!filterVerdict) return true;
  const approved = item.approved ?? 0;
  const rejected = item.rejected ?? 0;
  switch (filterVerdict) {
    case "approved":
      return approved > 0 && rejected === 0;
    case "rejected":
      return rejected > 0 && approved === 0;
    case "mixed":
      return approved > 0 && rejected > 0;
    default:
      return true;
  }
}

function computeCoverageSummary(items = []) {
  let bothVerified = 0;
  let webOnly = 0;
  let mobileOnly = 0;
  let unverified = 0;

  items.forEach((item) => {
    const webApproved = item.web_approved ?? 0;
    const mobileApproved = item.mobile_approved ?? 0;
    const webTotal = (item.web_approved ?? 0) + (item.web_rejected ?? 0);
    const mobileTotal = (item.mobile_approved ?? 0) + (item.mobile_rejected ?? 0);

    if (webApproved > 0 && mobileApproved > 0) bothVerified += 1;
    else if (webApproved > 0 && mobileTotal === 0) webOnly += 1;
    else if (mobileTotal > 0 && webTotal === 0) mobileOnly += 1;
    else if (webApproved === 0 && mobileApproved === 0) unverified += 1;
  });

  return { total: items.length, bothVerified, webOnly, mobileOnly, unverified };
}

const EDIT_HTML_FIELDS = new Set(["question_text", "explanation", "correct_answer", "correct_answer_text", "section_instructions"]);

function ImageThumb({ url }) {
  const [err, setErr] = useState(false);
  if (!url || err) return <Chip label="[img]" size="small" variant="outlined" sx={{ fontSize: "0.6rem", height: 18 }} />;
  return (
    <Box
      component="img"
      src={url}
      alt=""
      onError={() => setErr(true)}
      sx={{ height: 48, maxWidth: 80, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
      onClick={() => window.open(url, "_blank")}
    />
  );
}

function SubQuestionsValue({ value }) {
  if (!Array.isArray(value) || value.length === 0)
    return <Typography variant="caption" color="text.disabled">— (empty)</Typography>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {value.map((sq, i) => (
        <Box key={i} sx={{ p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.25 }}>
            Part {i + 1}{sq.type ? ` — ${sq.type}` : ""}
          </Typography>
          {sq.question_text && (
            <Box sx={{ "& p": { m: 0 }, fontSize: "0.8rem" }}>
              <HtmlWithLatex html={sq.question_text} />
            </Box>
          )}
          {sq.answer?.correct_answer != null && sq.answer.correct_answer !== "" && (
            <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600, display: "block", mt: 0.25 }}>
              Answer: {stripHtml(String(sq.answer.correct_answer))}
            </Typography>
          )}
          {sq.answer?.correct_answer_text && (
            <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600, display: "block", mt: 0.25 }}>
              Answer: {stripHtml(sq.answer.correct_answer_text)}
            </Typography>
          )}
          {sq.answer?.correct_blanks?.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: "wrap" }}>
              {sq.answer.correct_blanks.map((b, bi) => (
                <Chip key={bi} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: "0.58rem" }} />
              ))}
            </Stack>
          )}
          {sq.options?.length > 0 && (
            <Box component="ol" sx={{ m: 0, pl: 2, mt: 0.25 }}>
              {sq.options.map((o, oi) => (
                <li key={oi} style={{ fontSize: "0.75rem" }}>
                  {o?.image_url ? <ImageThumb url={o.image_url} /> : (o?.option_text ?? o?.text ?? "")}
                  {o?.is_correct && <Chip label="✓" size="small" color="success" sx={{ ml: 0.5, height: 14, fontSize: "0.55rem" }} />}
                </li>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

function ImageUrlsValue({ value }) {
  if (!value || (Array.isArray(value) && value.length === 0))
    return <Typography variant="caption" color="text.disabled">— (none)</Typography>;
  const urls = Array.isArray(value) ? value : [value];
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap">
      {urls.map((u, i) => <ImageThumb key={i} url={u} />)}
    </Stack>
  );
}

function AnswerValue({ value }) {
  const answerHtml = value?.correct_answer_text || value?.correct_answer || "";
  const blanks = Array.isArray(value?.correct_blanks) ? value.correct_blanks : [];
  const options = Array.isArray(value?.correct_options) ? value.correct_options : [];

  if (!answerHtml && blanks.length === 0 && options.length === 0) {
    return <Typography component="span" variant="caption" color="text.disabled">— (no answer)</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {answerHtml && <Box sx={{ "& p": { m: 0 } }}><HtmlWithLatex html={String(answerHtml)} /></Box>}
      {blanks.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {blanks.map((b, i) => <Chip key={i} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />)}
        </Stack>
      )}
      {options.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {options.map((o, i) => <Chip key={i} label={String(o)} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />)}
        </Stack>
      )}
    </Box>
  );
}

function EditFieldValue({ field, value }) {
  if (value === undefined || value === null || value === "") {
    return <Typography component="span" variant="caption" color="text.disabled">—</Typography>;
  }
  if (EDIT_HTML_FIELDS.has(field) && typeof value === "string") {
    return <Box sx={{ "& p": { m: 0 } }}><HtmlWithLatex html={value} /></Box>;
  }
  if ((field === "answer" || field === "answer_data") && typeof value === "object") {
    return <AnswerValue value={value} />;
  }
  if (field === "sub_questions") {
    return <SubQuestionsValue value={value} />;
  }
  if (field === "image_url" || field === "image_urls") {
    return <ImageUrlsValue value={value} />;
  }
  if (field === "options" && Array.isArray(value)) {
    if (value.length === 0) return <Typography variant="caption" color="text.disabled">— (empty)</Typography>;
    return (
      <Box component="ol" sx={{ m: 0, pl: 2 }}>
        {value.map((o, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {o?.image_url
              ? <ImageThumb url={o.image_url} />
              : <Typography component="span" variant="body2">{o?.option_text ?? o?.text ?? ""}</Typography>}
            {o?.is_correct && <Chip label="correct" size="small" color="success" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: "0.58rem" }} />}
          </li>
        ))}
      </Box>
    );
  }
  if (field === "correct_blanks" && Array.isArray(value)) {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {value.map((b, i) => <Chip key={i} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />)}
      </Stack>
    );
  }
  if (typeof value === "boolean") {
    return <Chip label={value ? "Yes" : "No"} size="small" color={value ? "success" : "default"} variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />;
  }
  if (typeof value === "object") {
    return <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(value, null, 1)}</Box>;
  }
  return <Typography component="span" variant="body2">{String(value)}</Typography>;
}

function EditDiffRow({ edit }) {
  const [expanded, setExpanded] = useState(false);
  const oldVals = edit.old || {};
  const newVals = edit.attributes || {};
  const keys = Array.from(new Set([...Object.keys(oldVals), ...Object.keys(newVals)]));
  const changeCount = keys.length;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{edit.user?.name ?? "—"}</Typography>
            {edit.user?.email && (
              <Typography variant="caption" color="text.secondary">{edit.user.email}</Typography>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>#{edit.subject_id}</TableCell>
        <TableCell>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {keys.map((k) => (
                <Chip key={k} label={fieldLabel(k)} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.62rem" }} />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {changeCount} field{changeCount !== 1 ? "s" : ""} changed
            </Typography>
          </Stack>
        </TableCell>
        <TableCell><PlatformChip platform={edit.platform} /></TableCell>
        <TableCell>
          {edit.verified_at ? (
            <Box>
              <Typography variant="body2">{new Date(edit.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(edit.verified_at).toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit" })}</Typography>
            </Box>
          ) : "—"}
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 1.5, my: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Before</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>After</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k}>
                      <TableCell sx={{ verticalAlign: "top", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {fieldLabel(k)}
                        <Typography variant="caption" display="block" color="text.disabled">{k}</Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", borderLeft: "3px solid", borderColor: "error.light", wordBreak: "break-word" }}>
                        <EditFieldValue field={k} value={oldVals[k]} />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", borderLeft: "3px solid", borderColor: "success.light", wordBreak: "break-word" }}>
                        <EditFieldValue field={k} value={newVals[k]} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function TesterEditsPanel({ edits, loading, error }) {
  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!edits || edits.length === 0) return <Alert severity="info">No question edits recorded for the selected filters.</Alert>;

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell sx={{ fontWeight: 700 }}>Tester</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Question ID</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Fields Changed</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {edits.map((e) => <EditDiffRow key={e.id} edit={e} />)}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const typeColors = {
  "multiple-choice": "#1976d2",
  "multiple-select": "#7b1fa2",
  "true-false": "#388e3c",
  "fill-in-the-blank": "#f57c00",
  "multi-fill": "#e64a19",
  matching: "#0097a7",
  ordering: "#5d4037",
  essay: "#c2185b",
  "short-answer": "#455a64",
  grouped: "#6d4c41",
};

const typeLabel = (t) => {
  const map = {
    "multiple-choice": "MCQ",
    "multiple-select": "Multi-select",
    "true-false": "True/False",
    "fill-in-the-blank": "Fill blank",
    "multi-fill": "Multi-fill",
    matching: "Matching",
    ordering: "Ordering",
    essay: "Essay",
    "short-answer": "Short ans.",
    grouped: "Grouped",
  };
  return map[t] || t;
};

export function AggregateChips({ aggregate }) {
  if (!aggregate || aggregate.total === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {aggregate.approved > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={aggregate.approved} color="success" size="small" variant="outlined" />}
      {aggregate.rejected > 0 && <Chip icon={<CancelOutlinedIcon />} label={aggregate.rejected} color="error" size="small" variant="outlined" />}
      {aggregate.flagged > 0 && <Chip label={`⚑ ${aggregate.flagged}`} color="warning" size="small" variant="outlined" />}
    </Stack>
  );
}

// ── Individual-record row ─────────────────────────────────────────────────────

function VerificationRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  // An unknown status must never look approved.
  const cfg = STATUS_CONFIG[row.status] ?? { label: row.status || "Unknown", color: "default", icon: null };
  return (
    <>
      <TableRow hover>
        <TableCell>
          <Chip label={row.verifiable_type} size="small" variant="outlined" />
        </TableCell>
        <TableCell>{row.verifiable_id}</TableCell>
        <TableCell>{row.user?.name ?? row.user_id}</TableCell>
        <TableCell>
          <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" />
        </TableCell>
        <TableCell><PlatformChip platform={row.platform} /></TableCell>
        <TableCell sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <Tooltip title={row.notes ?? ""}>
            <span>{row.notes || "—"}</span>
          </Tooltip>
        </TableCell>
        <TableCell>{row.verified_at ? new Date(row.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}</TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, my: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Full Notes</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{row.notes || "No notes provided."}</Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Grouped-by-item row ───────────────────────────────────────────────────────

const decodeSnippet = (s) => {
  if (!s) return '';
  try {
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  } catch {
    return s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }
};

function ItemAggregateRow({ item, onView, onEdit, onVerify }) {
  const [expanded, setExpanded] = useState(false);
  const testerRows = groupVerificationsByTester(item.verifications);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Stack spacing={0.5}>
            <Chip label={item.verifiable_type} size="small" variant="outlined" />
            {item.question_data?.type && (
              <Chip
                label={typeLabel(item.question_data.type)}
                size="small"
                sx={{
                  fontSize: "0.62rem",
                  height: 18,
                  bgcolor: (typeColors[item.question_data.type] || "#555") + "18",
                  color: typeColors[item.question_data.type] || "#555",
                  fontWeight: 600,
                  border: "none",
                }}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={0.3}>
            <Typography sx={{ fontFamily: "monospace", fontWeight: 700 }}>#{item.verifiable_id}</Typography>
            {item.question_data?.text_snippet && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", maxWidth: 280, display: "block" }}>
                {decodeSnippet(item.question_data.text_snippet)}
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <AggregateChips aggregate={{ approved: item.approved, rejected: item.rejected, total: item.total }} />
        </TableCell>
        <TableCell>
          <PlatformCoverageChips item={item} />
        </TableCell>
        <TableCell>
          <Chip
            label={`${item.total} tester${item.total !== 1 ? "s" : ""}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem" }}
          />
        </TableCell>
        <TableCell>{item.last_verified_at ? new Date(item.last_verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}</TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {item.verifiable_type === "question" && (
              <>
                <Tooltip title="Accept / reject">
                  <IconButton size="small" onClick={() => onVerify?.(item)} sx={{ color: "success.main" }}>
                    <FactCheckOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View question">
                  <IconButton size="small" onClick={() => onView?.(item.verifiable_id)} sx={{ color: "#3f2b96" }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit question">
                  <IconButton size="small" onClick={() => onEdit?.(item.verifiable_id)} sx={{ color: "primary.main" }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 1.5, my: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                Tester verdicts
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Tester</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Web</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testerRows.map((row) => {
                    const latest = [row.web, row.mobile]
                      .filter(Boolean)
                      .sort((a, b) => new Date(b.verified_at) - new Date(a.verified_at))[0];
                    return (
                      <TableRow key={row.name}>
                        <TableCell sx={{ py: 0.75, fontWeight: 600 }}>{row.name}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <VerdictChip verdict={row.web?.status} />
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <VerdictChip verdict={row.mobile?.status} />
                        </TableCell>
                        <TableCell sx={{ py: 0.75, maxWidth: 320, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {row.notes.length > 0
                            ? <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{row.notes.join("\n")}</Typography>
                            : <Typography variant="caption" color="text.disabled">—</Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {latest?.verified_at ? new Date(latest.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Paper coverage panel ──────────────────────────────────────────────────────

function CoverageBar({ verified, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((verified / total) * 100)) : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 160 }}>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "action.hover", overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: pct >= 100 ? "success.main" : "primary.main", transition: "width .2s" }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34, textAlign: "right" }}>{pct}%</Typography>
    </Box>
  );
}

// Per-platform coverage under the Verified/Total figure — makes a web-only paper obvious instead
// of letting it read as fully verified.
function PlatformCoverage({ row }) {
  const platforms = row.required_platforms ?? ["web", "mobile"];
  if (platforms.length < 2) return null;
  const verified = row.platform_verified ?? {};
  const pending = row.platform_pending ?? {};
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
      {platforms.map((pf) => (
        <Chip
          key={pf}
          label={`${pf}: ${verified[pf] ?? 0}${pending[pf] ? ` (${pending[pf]} pending)` : ""}`}
          size="small"
          variant="outlined"
          color={pending[pf] ? "warning" : "default"}
          sx={{ height: 18, fontSize: "0.6rem" }}
        />
      ))}
    </Stack>
  );
}

const fmtCount = (n) => Number(n ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const VERIFIER_SECTIONS = [
  { key: "papers", label: "Papers", tab: "papers" },
  { key: "mock_exams", label: "Mock exams", tab: "mock-exams" },
  { key: "worksheets", label: "Worksheets", tab: "worksheets" },
];

// "Verified by" summary: what the verifier chosen in the filter verified, per section.
// Counts questions with at least one verdict by that verifier (on the chosen platform, or any),
// so a web-only verifier such as Grokbot QA shows up even though both platforms are in scope.
export function VerifierSummaryCard({ summary, loading, error, onOpenSection }) {
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!summary) {
    return loading ? (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} /> <Typography variant="body2">Counting verified questions…</Typography>
      </Paper>
    ) : null;
  }
  const q = summary.questions ?? {};
  const platformLabel = summary.platform ? ` on ${summary.platform}` : " (any platform)";
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} data-testid="verifier-summary">
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <VerifiedIcon color="primary" fontSize="small" />
        <Typography sx={{ fontWeight: 700 }}>
          Verified by {summary.verifier?.name ?? "—"}{platformLabel}
        </Typography>
        <Chip label={`${fmtCount(q.questions)} questions`} color="primary" size="small" />
        {q.approved > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={`${fmtCount(q.approved)} approved`} color="success" size="small" variant="outlined" />}
        {q.rejected > 0 && <Chip icon={<CancelOutlinedIcon />} label={`${fmtCount(q.rejected)} rejected`} color="error" size="small" variant="outlined" />}
        {q.flagged > 0 && <Chip label={`${fmtCount(q.flagged)} flagged`} color="warning" size="small" variant="outlined" />}
        {loading && <CircularProgress size={14} />}
      </Stack>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {VERIFIER_SECTIONS.map(({ key, label, tab }) => {
          const sec = summary.sections?.[key];
          if (!sec) return null;
          return (
            <Paper
              key={key}
              variant="outlined"
              onClick={() => onOpenSection?.(tab)}
              data-testid={`verifier-summary-${key}`}
              sx={{ p: 1.5, flex: "1 1 240px", cursor: onOpenSection ? "pointer" : "default", "&:hover": { bgcolor: "action.hover" } }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>{label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {fmtCount(sec.questions_verified)}
                <Typography component="span" variant="body2" color="text.secondary"> / {fmtCount(sec.questions_total)} questions</Typography>
              </Typography>
              <CoverageBar verified={sec.questions_verified} total={sec.questions_total} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Every question verified in {fmtCount(sec.items_all_verified)} of {fmtCount(sec.items)} · some in {fmtCount(sec.items_with_verifier)} · none in {fmtCount(sec.items_none_verified)}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}

function PaperCoveragePanel({ papers, loading, error, onViewPaper, onOpenQuestion, openingQuestion, entityLabel = "paper", verifierName = null }) {
  const [expanded, setExpanded] = useState(null);
  const entityTitle = entityLabel.replace(/\b\w/g, (c) => c.toUpperCase());
  const showVerifier = Boolean(verifierName) && (papers ?? []).some((p) => p.verifier_verified_questions !== undefined);
  const colCount = showVerifier ? 8 : 7;

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!papers || papers.length === 0) return <Alert severity="info">No {entityLabel}s found for the selected filters.</Alert>;

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell sx={{ fontWeight: 700 }}>{entityTitle}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Coverage</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Verified / Total</TableCell>
            {showVerifier && <TableCell sx={{ fontWeight: 700 }}>Verified by {verifierName}</TableCell>}
            <TableCell sx={{ fontWeight: 700 }}>Verdicts</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Testers</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {papers.map((p) => {
            const isOpen = expanded === p.paper_id;
            return (
              <React.Fragment key={p.paper_id}>
                <TableRow hover sx={p.fully_verified ? { bgcolor: "rgba(46,125,50,0.05)" } : undefined}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{p.paper_name}</Typography>
                    {p.shelf === "practice" && <Chip label="Practice Paper" size="small" color="secondary" variant="outlined" sx={{ mt: 0.25 }} />}
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>#{p.paper_id}</Typography>
                      {entityLabel === "worksheet" && p.difficulty && (
                        <Chip
                          label={p.difficulty}
                          size="small"
                          variant="outlined"
                          color={difficultyChipColor(p.difficulty)}
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell><CoverageBar verified={p.verified_questions} total={p.total_questions} /></TableCell>
                  <TableCell>
                    <Tooltip title="Verified on every platform in scope — a web-only verdict does not count">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.verified_questions} / {p.total_questions}
                      </Typography>
                    </Tooltip>
                    <PlatformCoverage row={p} />
                  </TableCell>
                  {showVerifier && (
                    <TableCell data-testid="verifier-cell">
                      <Tooltip title={`Questions in this ${entityLabel} with a verdict by ${verifierName}`}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p.verifier_verified_questions ?? 0} / {p.total_questions}
                        </Typography>
                      </Tooltip>
                      {p.verifier_all_verified
                        ? <Chip icon={<CheckCircleOutlineIcon />} label="All" color="success" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                        : (p.verifier_verified_questions ?? 0) === 0
                          ? <Chip label="None" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                          : null}
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {p.approved_questions > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={p.approved_questions} color="success" size="small" variant="outlined" />}
                      {p.rejected_questions > 0 && <Chip icon={<CancelOutlinedIcon />} label={p.rejected_questions} color="error" size="small" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {p.fully_verified
                        ? <Chip icon={<CheckCircleOutlineIcon />} label="Fully verified" color="success" size="small" />
                        : (p.verified_questions > 0 || p.partly_verified_questions > 0)
                          ? <Chip label="Partial" color="warning" size="small" variant="outlined" />
                          : <Chip label="Not started" size="small" variant="outlined" />}
                      {p.paper_approved && (
                        <Tooltip title={`Signed off by ${(p.paper_sign_offs ?? []).map((s) => s.user?.name).filter(Boolean).join(", ") || "a tester"}`}>
                          <Chip icon={<VerifiedIcon />} label={entityLabel === "worksheet" ? "Worksheet approved" : "Paper approved"} color="success" size="small" variant="outlined" />
                        </Tooltip>
                      )}
                      {!p.paper_approved && (p.paper_approved_platforms?.length ?? 0) > 0 && (
                        <Chip
                          label={`Signed off on ${p.paper_approved_platforms.join(", ")} only`}
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {p.paper_rejected && <Chip icon={<CancelOutlinedIcon />} label={entityLabel === "worksheet" ? "Worksheet rejected" : "Paper rejected"} color="error" size="small" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={`${p.verifiers?.length ?? 0} tester${(p.verifiers?.length ?? 0) !== 1 ? "s" : ""}`} size="small" variant="outlined" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                      {onViewPaper && (
                        <Tooltip title={`View this ${entityLabel}'s questions (Question Bank)`}>
                          <IconButton size="small" onClick={() => onViewPaper(p.paper_id, p)} sx={{ color: "primary.main" }}>
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" onClick={() => setExpanded(isOpen ? null : p.paper_id)}>
                        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ py: 0 }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ p: 1.5, my: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
                        {p.paper_sign_offs?.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}>
                              Whole-{entityLabel} sign-off
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {p.paper_sign_offs.map((s, i) => (
                                <Chip
                                  key={s.user?.id ?? i}
                                  icon={s.status === "approved" ? <VerifiedIcon /> : <CancelOutlinedIcon />}
                                  label={`${s.user?.name ?? "—"} · ${s.status}${s.verified_at ? ` · ${s.verified_at}` : ""}`}
                                  size="small"
                                  color={s.status === "approved" ? "success" : "error"}
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                        {p.unverified_questions?.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}>
                              Not verified yet ({p.unverified_questions.length}) — click a number to open it for verification.
                              The platform tag is what is still missing.
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {p.unverified_questions.map((q) => {
                                const missing = q.missing ?? p.required_platforms ?? [];
                                const partial = missing.length === 1 && (p.required_platforms ?? ["web", "mobile"]).length > 1;
                                return (
                                <Chip
                                  key={q.id}
                                  label={`${q.number ? `Q${q.number}` : `#${q.id}`}${partial ? ` · ${missing[0]}` : ""}`}
                                  size="small"
                                  color="warning"
                                  variant={partial ? "filled" : "outlined"}
                                  disabled={openingQuestion === q.id}
                                  onClick={() => onOpenQuestion?.(p.paper_id, q)}
                                  sx={{ cursor: "pointer", fontWeight: 700 }}
                                />
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                          Questions verified per tester
                        </Typography>
                        {p.verifiers?.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Tester</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Questions Verified</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {p.verifiers.map((v, i) => (
                                <TableRow key={v.user?.id ?? i}>
                                  <TableCell sx={{ py: 0.75, fontWeight: 600 }}>{v.user?.name ?? "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}>
                                    <Chip label={v.verified_count} size="small" color="primary" variant="outlined" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No tester breakdown available.</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Tester stats panel ────────────────────────────────────────────────────────

const QB_FILTER_KEY = "qb_list_filters";

function TesterStatsPanel({ stats, loading, error, onShowItems }) {
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  const handleViewQuestions = (userId) => {
    try {
      const existing = JSON.parse(sessionStorage.getItem(QB_FILTER_KEY) || "{}");
      sessionStorage.setItem(QB_FILTER_KEY, JSON.stringify({ ...existing, filterTesterUserId: String(userId), page: 0 }));
    } catch {}
    navigate("/admin/question-bank");
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats || stats.length === 0) return <Alert severity="info">No verification data found.</Alert>;

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell sx={{ fontWeight: 700 }}>Tester</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Approved</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Rejected</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Flagged</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Total Verdicts</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Unique Items</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {stats.map((s, idx) => {
            const key = s.user?.id ?? idx;
            const isOpen = expanded === key;
            return (
              <React.Fragment key={key}>
                <TableRow
                  hover
                  sx={s.rejected > 0 ? { bgcolor: "rgba(211,47,47,0.04)" } : undefined}
                >
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{s.user?.name ?? "—"}</Typography>
                      {s.user?.email && <Typography variant="caption" color="text.secondary">{s.user.email}</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {s.approved > 0
                      ? <Chip icon={<CheckCircleOutlineIcon />} label={s.approved} color="success" size="small" variant="outlined" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {s.rejected > 0
                      ? <Chip icon={<CancelOutlinedIcon />} label={s.rejected} color="error" size="small" variant="outlined" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    {s.flagged > 0
                      ? <Chip label={s.flagged} color="warning" size="small" variant="outlined" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.total} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={s.unique_items} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                      {s.user?.id && (
                        <Tooltip title="View / edit their verified items (By Item)">
                          <IconButton size="small" onClick={() => onShowItems?.(s.user.id)} sx={{ color: "success.main" }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {s.user?.id && (
                        <Tooltip title="View their questions in Question Bank">
                          <IconButton size="small" onClick={() => handleViewQuestions(s.user.id)} sx={{ color: "primary.main" }}>
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" onClick={() => setExpanded(isOpen ? null : key)}>
                        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0 }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ p: 1.5, my: 0.5, bgcolor: "action.hover", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                          Breakdown by type
                        </Typography>
                        {s.by_type?.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Approved</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Rejected</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Flagged</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {s.by_type.map((t) => (
                                <TableRow key={t.type}>
                                  <TableCell sx={{ py: 0.75 }}><Chip label={t.type} size="small" variant="outlined" /></TableCell>
                                  <TableCell sx={{ py: 0.75 }}>{t.approved > 0 ? <Chip icon={<CheckCircleOutlineIcon />} label={t.approved} color="success" size="small" variant="outlined" /> : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}>{t.rejected > 0 ? <Chip icon={<CancelOutlinedIcon />} label={t.rejected} color="error" size="small" variant="outlined" /> : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}>{t.flagged > 0 ? <Chip label={t.flagged} color="warning" size="small" variant="outlined" /> : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}><Chip label={t.total} size="small" variant="outlined" /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No breakdown available.</Typography>
                        )}
                        {s.by_platform?.length > 0 && (
                          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>By platform:</Typography>
                            {s.by_platform.map((p) => (
                              <Chip key={p.platform} label={`${p.platform}: ${p.total}`} size="small" variant="outlined" color={PLATFORM_COLORS[p.platform] || "default"} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

const localDateIso = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDateIso(d);
};

const todayIso = () => localDateIso();
const PAGE_SIZE = 25;

function DashboardPagination({ page, totalPages, onChange, total }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, flexWrap: "wrap", gap: 1 }}>
      {total != null && (
        <Typography variant="caption" color="text.secondary">
          {total} record{total !== 1 ? "s" : ""} total
        </Typography>
      )}
      <Pagination
        count={totalPages}
        page={page}
        onChange={onChange}
        color="primary"
        showFirstButton
        showLastButton
      />
    </Box>
  );
}

function groupByTester(rows) {
  const map = {};
  rows.forEach((r) => {
    const name = r.user?.name ?? `User #${r.user_id}`;
    if (!map[name]) map[name] = { name, approved: 0, rejected: 0, items: [] };
    if (r.status === "approved") map[name].approved += 1;
    else if (r.status === "rejected") map[name].rejected += 1;
    map[name].items.push(r);
  });
  return Object.values(map).sort((a, b) => (b.approved + b.rejected) - (a.approved + a.rejected));
}

function TesterActivityPanel({ rows, loading, error }) {
  const [expandedTester, setExpandedTester] = useState(null);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const groups = groupByTester(rows);
  if (groups.length === 0) return <Alert severity="info">No verifications recorded today.</Alert>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {groups.map((g) => {
        const isOpen = expandedTester === g.name;
        return (
          <Paper key={g.name} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setExpandedTester(isOpen ? null : g.name)}
              sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 2, cursor: "pointer", bgcolor: isOpen ? "action.selected" : "background.paper", "&:hover": { bgcolor: "action.hover" } }}
            >
              <Typography sx={{ fontWeight: 700, flex: 1 }}>{g.name}</Typography>
              <Stack direction="row" spacing={1}>
                {g.approved > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={`${g.approved} Approved`} color="success" size="small" variant="outlined" />}
                {g.rejected > 0 && <Chip icon={<CancelOutlinedIcon />} label={`${g.rejected} Rejected`} color="error" size="small" variant="outlined" />}
                <Chip label={`${g.approved + g.rejected} total`} size="small" variant="outlined" />
              </Stack>
              {isOpen ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
            </Box>
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ px: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Item ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Verdict</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.items.map((r) => {
                      const cfg = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.approved;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell><Chip label={r.verifiable_type} size="small" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>#{r.verifiable_id}</TableCell>
                          <TableCell><Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" /></TableCell>
                          <TableCell><PlatformChip platform={r.platform} /></TableCell>
                          <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <Tooltip title={r.notes ?? ""}><span>{r.notes || "—"}</span></Tooltip>
                          </TableCell>
                          <TableCell>{r.verified_at ? new Date(r.verified_at).toLocaleTimeString('en-GB', { hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
}

export default function VerificationDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("items");
  const isWorksheetTab = tab === "worksheets";
  const isMockExamTab = tab === "mock-exams";
  const isCoverageTab = tab === "papers" || isWorksheetTab || isMockExamTab;
  const coverageEntity = isWorksheetTab ? "worksheet" : isMockExamTab ? "mock exam" : "paper";
  const coverageEntityTitle = coverageEntity.replace(/\b\w/g, (c) => c.toUpperCase());

  // ── Records view state
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState(null);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsTotalPages, setRowsTotalPages] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);

  // ── Items (grouped) view state
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState(null);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsTotalPages, setItemsTotalPages] = useState(1);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsSummary, setItemsSummary] = useState({ total: 0, both_verified: 0, web_only: 0, mobile_only: 0, unverified: 0 });

  // ── Tester activity (today)
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);

  // ── Papers (paper-wise coverage) view state
  const [papers, setPapers] = useState([]);
  const [papersLoading, setPapersLoading] = useState(false);
  const [papersError, setPapersError] = useState(null);
  const [papersPage, setPapersPage] = useState(1);
  const [papersTotalPages, setPapersTotalPages] = useState(1);
  const [papersTotal, setPapersTotal] = useState(0);
  const [papersSummary, setPapersSummary] = useState({ total: 0, fully_verified: 0, partially_verified: 0, not_started: 0, paper_approved: 0 });
  const [filterPaperCoverage, setFilterPaperCoverage] = useState("all");
  const [filterPaperSubjectId, setFilterPaperSubjectId] = useState("");
  const [filterPaperShelf, setFilterPaperShelf] = useState("");
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [openingQuestionId, setOpeningQuestionId] = useState(null);
  const [filterPaperSearch, setFilterPaperSearch] = useState("");
  const [filterExamCategoryId, setFilterExamCategoryId] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("");
  const [examOptions, setExamOptions] = useState([]);
  const [examCategoryOptions, setExamCategoryOptions] = useState([]);

  // ── Tester stats (all-time)
  const [testerStats, setTesterStats] = useState([]);
  const [testerStatsLoading, setTesterStatsLoading] = useState(false);
  const [testerStatsError, setTesterStatsError] = useState(null);
  const [statsPage, setStatsPage] = useState(1);
  const [statsTotalPages, setStatsTotalPages] = useState(1);
  const [statsTotal, setStatsTotal] = useState(0);

  // ── Question view / edit dialogs
  const [viewQDialogOpen, setViewQDialogOpen] = useState(false);
  const [viewQData, setViewQData] = useState(null);
  const [viewQLoading, setViewQLoading] = useState(false);
  const [viewQVerifications, setViewQVerifications] = useState([]);
  const [viewQVerificationsLoading, setViewQVerificationsLoading] = useState(false);
  const [editQOpen, setEditQOpen] = useState(false);
  const [editQData, setEditQData] = useState(null);
  const [editQSaving, setEditQSaving] = useState(false);

  // ── Verify (accept/reject) dialog
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyItem, setVerifyItem] = useState(null);
  const [verifyQuestion, setVerifyQuestion] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifySubmitting, setVerifySubmitting] = useState(false);

  // Tester filter options
  const [testerOptions, setTesterOptions] = useState([]);
  const [filterTesterUserId, setFilterTesterUserId] = useState("");
  const [verifierSummary, setVerifierSummary] = useState(null);
  const [verifierSummaryLoading, setVerifierSummaryLoading] = useState(false);
  const [verifierSummaryError, setVerifierSummaryError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("common/data?param=Subjects")
      .then((res) => setSubjectOptions(res.data?.data ?? []))
      .catch(() => {});
    api.get("admin/content-verifications/verifiers").then((res) => {
      if (!cancelled) setTesterOptions(Array.isArray(res.data?.data) ? res.data.data : []);
    }).catch(() => {});
    // The tree endpoint is the only one that reaches child categories; /categories is top-level only.
    api.get("admin/mock-exam/category-tree")
      .then((res) => { if (!cancelled) setExamCategoryOptions(flattenExamCategories(res.data?.data ?? [])); })
      .catch(() => {
        api.get("admin/mock-exam/categories")
          .then((res) => { if (!cancelled) setExamCategoryOptions(res.data?.data ?? []); })
          .catch(() => {});
      });
    return () => { cancelled = true; };
  }, []);

  // Mock exam names for the By Mock Exam autocomplete — debounced as the admin types.
  const examSearchTimer = useRef(null);
  useEffect(() => {
    if (!isMockExamTab) return undefined;
    clearTimeout(examSearchTimer.current);
    examSearchTimer.current = setTimeout(async () => {
      try {
        const params = { per_page: 50, order_by: "name", order_dir: "asc" };
        if (filterPaperSearch) params.search = filterPaperSearch;
        if (filterPaperSubjectId) params.subject_id = filterPaperSubjectId;
        if (filterExamCategoryId) params.category_id = filterExamCategoryId;
        const res = await api.get("/admin/mock-exam", { params });
        const items = res.data?.data?.data ?? res.data?.data ?? [];
        setExamOptions(items.map((e) => ({ id: e.id, name: e.name })));
      } catch {
        // silently ignore — the dropdown just stays empty and free text still filters
      }
    }, 300);
    return () => clearTimeout(examSearchTimer.current);
  }, [isMockExamTab, filterPaperSearch, filterPaperSubjectId, filterExamCategoryId]);

  // Shared filters
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterCoverage, setFilterCoverage] = useState("");
  const [filterVerdict, setFilterVerdict] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState(() => daysAgoIso(30));
  const [filterDateTo, setFilterDateTo] = useState(() => localDateIso());
  const [minVerifiers, setMinVerifiers] = useState("");

  // ── Tester edits (question.updated history)
  const [edits, setEdits] = useState([]);
  const [editsLoading, setEditsLoading] = useState(false);
  const [editsError, setEditsError] = useState(null);
  const [editsPage, setEditsPage] = useState(1);
  const [editsTotalPages, setEditsTotalPages] = useState(1);
  const [editsTotal, setEditsTotal] = useState(0);
  const [filterQuestionId, setFilterQuestionId] = useState("");

  const resetAllPages = useCallback(() => {
    setRowsPage(1);
    setItemsPage(1);
    setActivityPage(1);
    setEditsPage(1);
    setStatsPage(1);
    setPapersPage(1);
  }, []);

  const resetQuestionId = () => { setFilterQuestionId(""); setEditsPage(1); };

  const fetchRows = useCallback(async (pg = 1) => {
    setRowsLoading(true);
    setRowsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (filterPlatform) params.platform = filterPlatform;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (minVerifiers) params.min_verifiers = minVerifiers;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      const res = await api.get("admin/content-verifications", { params });
      if (res.data?.success) {
        const d = res.data.data;
        setRows(d?.verifications ?? d?.data ?? []);
        setRowsTotalPages(d?.pagination?.last_page ?? d?.last_page ?? 1);
        setRowsTotal(d?.pagination?.total ?? d?.total ?? 0);
      }
    } catch {
      setRowsError("Failed to load verifications.");
    } finally {
      setRowsLoading(false);
    }
  }, [filterType, filterStatus, filterPlatform, filterDateFrom, filterDateTo, minVerifiers, filterTesterUserId]);

  const fetchItems = useCallback(async (pg = 1) => {
    setItemsLoading(true);
    setItemsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterPlatform) params.platform = filterPlatform;
      if (minVerifiers) params.min_verifiers = minVerifiers;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      if (filterQuestionId) params.verifiable_id = filterQuestionId;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterCoverage) params.coverage = filterCoverage;
      if (filterVerdict) params.verdict = filterVerdict;
      const res = await api.get("admin/content-verifications/item-aggregates", { params });
      if (res.data?.success) {
        const d = res.data.data;
        setItems(d?.data ?? []);
        setItemsTotalPages(d?.last_page ?? 1);
        setItemsTotal(d?.total ?? 0);
        setItemsSummary(d?.summary ?? { total: 0, both_verified: 0, web_only: 0, mobile_only: 0, unverified: 0 });
      }
    } catch {
      setItemsError("Failed to load item aggregates.");
    } finally {
      setItemsLoading(false);
    }
  }, [filterType, filterPlatform, minVerifiers, filterTesterUserId, filterQuestionId, filterDateFrom, filterDateTo, filterCoverage, filterVerdict]);

  // Opens the paper/worksheet in the student take view (the only place a question can be verified)
  // parked on the unverified question. Worksheets take their own id; papers need a purchase row,
  // which /start mints for a verifier.
  const openQuestionForVerification = useCallback(async (entityId, question) => {
    setOpeningQuestionId(question.id);
    if (isWorksheetTab) {
      navigate(`/${STUDENT_BASE}/work-sheet/${entityId}/take`, { state: { jumpToQuestionId: question.id } });
      setOpeningQuestionId(null);
      return;
    }
    if (isMockExamTab) {
      navigate(`/${STUDENT_BASE}/mock-exam/${entityId}/take`, { state: { jumpToQuestionId: question.id } });
      setOpeningQuestionId(null);
      return;
    }
    try {
      let purchaseId = null;
      try {
        const res = await api.post(`/student/paper/${entityId}/start`);
        purchaseId = res.data?.data?.purchase_id ?? res.data?.purchase_id;
      } catch (startErr) {
        // "Already started" / "already completed" still leaves a usable purchase to resume.
        const res = await api.get("/student/paper/my-purchases");
        const list = res.data?.data?.data ?? res.data?.data ?? [];
        const match = (Array.isArray(list) ? list : []).find((p) => Number(p.paper_id) === Number(entityId));
        purchaseId = match?.id ?? match?.purchase_id ?? null;
        if (!purchaseId) throw startErr;
      }
      if (!purchaseId) {
        toast.error("Could not open this paper.");
        return;
      }
      navigate(`/${STUDENT_BASE}/past-paper/${purchaseId}/take`, {
        state: { jumpToQuestionId: question.id },
      });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not open this paper.");
    } finally {
      setOpeningQuestionId(null);
    }
  }, [navigate, isWorksheetTab, isMockExamTab]);

  const fetchPapers = useCallback(async (pg = 1) => {
    setPapersLoading(true);
    setPapersError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterPlatform) params.platform = filterPlatform;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterPaperCoverage) params.coverage = filterPaperCoverage;
      if (filterPaperSubjectId) params.subject_id = filterPaperSubjectId;
      if (filterPaperSearch) params.search = filterPaperSearch;
      if (isMockExamTab && filterExamCategoryId) params.category_id = filterExamCategoryId;
      if (!isMockExamTab && !isWorksheetTab && filterPaperShelf) params.shelf = filterPaperShelf;
      if (isWorksheetTab && filterDifficulty) params.difficulty = filterDifficulty;
      const endpoint = isWorksheetTab
        ? "admin/content-verifications/worksheet-aggregates"
        : isMockExamTab
          ? "admin/content-verifications/mock-exam-aggregates"
          : "admin/content-verifications/paper-aggregates";
      const res = await api.get(endpoint, { params });
      if (res.data?.success) {
        const d = res.data.data;
        setPapers((d?.data ?? []).map((r) => ({
          ...r,
          paper_id: r.paper_id ?? r.worksheet_id ?? r.mock_exam_id,
          paper_name: r.paper_name ?? r.worksheet_name ?? r.mock_exam_name,
        })));
        setPapersTotalPages(d?.last_page ?? 1);
        setPapersTotal(d?.total ?? 0);
        setPapersSummary(d?.summary ?? { total: 0, fully_verified: 0, partially_verified: 0, not_started: 0, paper_approved: 0 });
      }
    } catch {
      // Clear the counts too — the three coverage tabs share this state, so a failed load would
      // otherwise leave the previous tab's totals sitting under the new tab's labels.
      setPapers([]);
      setPapersTotal(0);
      setPapersTotalPages(1);
      setPapersSummary({ total: 0, fully_verified: 0, partially_verified: 0, not_started: 0, paper_approved: 0 });
      setPapersError(`Failed to load ${coverageEntity} coverage.`);
    } finally {
      setPapersLoading(false);
    }
  }, [isWorksheetTab, isMockExamTab, coverageEntity, filterPlatform, filterTesterUserId, filterDateFrom, filterDateTo, filterPaperCoverage, filterPaperSubjectId, filterPaperShelf, filterPaperSearch, filterExamCategoryId, filterDifficulty]);

  // "Verified by" summary across papers, mock exams and worksheets for the chosen verifier.
  useEffect(() => {
    if (!filterTesterUserId) {
      setVerifierSummary(null);
      setVerifierSummaryError(null);
      return undefined;
    }
    let cancelled = false;
    setVerifierSummaryLoading(true);
    setVerifierSummaryError(null);
    const params = { user_id: filterTesterUserId };
    if (filterPlatform) params.platform = filterPlatform;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    api.get("admin/content-verifications/verifier-summary", { params })
      .then((res) => { if (!cancelled) setVerifierSummary(res.data?.success ? res.data.data : null); })
      .catch(() => { if (!cancelled) { setVerifierSummary(null); setVerifierSummaryError("Could not load the Verified by summary."); } })
      .finally(() => { if (!cancelled) setVerifierSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [filterTesterUserId, filterPlatform, filterDateFrom, filterDateTo]);

  // The verifier-only coverage choices make no sense once the verifier is cleared.
  useEffect(() => {
    if (!filterTesterUserId && String(filterPaperCoverage).startsWith("verifier_")) {
      setFilterPaperCoverage("all");
    }
  }, [filterTesterUserId, filterPaperCoverage]);

  const selectedVerifierName = filterTesterUserId
    ? (testerOptions.find((o) => String(o.id) === String(filterTesterUserId))?.name ?? verifierSummary?.verifier?.name ?? `User #${filterTesterUserId}`)
    : null;

  const fetchActivity = useCallback(async (pg = 1) => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const params = { date_from: filterDateFrom || todayIso(), date_to: filterDateTo || todayIso(), page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterPlatform) params.platform = filterPlatform;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      const res = await api.get("admin/content-verifications", { params });
      if (res.data?.success) {
        const d = res.data.data;
        setActivityRows(d?.verifications ?? d?.data ?? []);
        setActivityTotalPages(d?.pagination?.last_page ?? d?.last_page ?? 1);
        setActivityTotal(d?.pagination?.total ?? d?.total ?? 0);
      }
    } catch {
      setActivityError("Failed to load today's activity.");
    } finally {
      setActivityLoading(false);
    }
  }, [filterType, filterPlatform, filterTesterUserId, filterDateFrom, filterDateTo]);

  const fetchTesterStats = useCallback(async (pg = 1) => {
    setTesterStatsLoading(true);
    setTesterStatsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterPlatform) params.platform = filterPlatform;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await api.get("admin/content-verifications/tester-stats", { params });
      if (res.data?.success) {
        const d = res.data.data;
        setTesterStats(d?.data ?? (Array.isArray(d) ? d : []));
        setStatsTotalPages(d?.last_page ?? 1);
        setStatsTotal(d?.total ?? 0);
      }
    } catch {
      setTesterStatsError("Failed to load tester stats.");
    } finally {
      setTesterStatsLoading(false);
    }
  }, [filterType, filterPlatform, filterDateFrom, filterDateTo]);

  const fetchEdits = useCallback(async (pg = 1) => {
    setEditsLoading(true);
    setEditsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterPlatform) params.platform = filterPlatform;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      if (filterQuestionId) params.verifiable_id = filterQuestionId;
      const res = await api.get("admin/content-verifications/edits", { params });
      if (res.data?.success) {
        const d = res.data.data;
        setEdits(d?.data ?? []);
        setEditsTotalPages(d?.last_page ?? 1);
        setEditsTotal(d?.total ?? 0);
      }
    } catch {
      setEditsError("Failed to load tester edits.");
    } finally {
      setEditsLoading(false);
    }
  }, [filterPlatform, filterDateFrom, filterDateTo, filterTesterUserId, filterQuestionId]);

  const handleLoadQuestion = useCallback(async (id, mode) => {
    if (mode === "view") {
      setViewQLoading(true);
      setViewQDialogOpen(true);
      setViewQData(null);
      setViewQVerifications([]);
      setViewQVerificationsLoading(true);
    }
    try {
      const [qRes, vRes] = await Promise.all([
        api.get(`/admin/question-bank/${id}`),
        mode === "view"
          ? api.get("admin/content-verifications", { params: { type: "question", verifiable_id: id, per_page: 50 } }).catch(() => null)
          : Promise.resolve(null),
      ]);
      const q = qRes.data?.data;
      if (mode === "view") {
        setViewQData(q);
        const d = vRes?.data?.data;
        setViewQVerifications(d?.verifications ?? d?.data ?? []);
      } else {
        setEditQData(q);
        setEditQOpen(true);
      }
    } catch {
      toast.error("Failed to load question");
      if (mode === "view") setViewQDialogOpen(false);
    } finally {
      if (mode === "view") {
        setViewQLoading(false);
        setViewQVerificationsLoading(false);
      }
    }
  }, []);

  const handleOpenVerify = useCallback(async (item) => {
    setVerifyItem(item);
    setVerifyQuestion(null);
    setVerifyOpen(true);
    setVerifyLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/${item.verifiable_id}`);
      const q = res.data?.data;
      setVerifyQuestion(q ? { ...q, paper_number: q.paper_number ?? q.id } : null);
    } catch {
      toast.error("Failed to load question");
    } finally {
      setVerifyLoading(false);
    }
  }, []);

  const handleSubmitVerify = useCallback(async ({ status, notes, platforms }) => {
    if (!verifyItem) return;
    setVerifySubmitting(true);
    try {
      await api.post("admin/content-verifications", {
        verifiable_type: verifyItem.verifiable_type,
        verifiable_id: verifyItem.verifiable_id,
        status,
        notes,
        platforms,
      });
      toast.success(status === "approved" ? "Marked as approved." : "Marked as rejected.");
      setVerifyOpen(false);
      setVerifyItem(null);
      setVerifyQuestion(null);
      fetchItems(itemsPage);
    } catch {
      toast.error("Failed to save verdict.");
    } finally {
      setVerifySubmitting(false);
    }
  }, [verifyItem, fetchItems, itemsPage]);

  const verifyItemData = (() => {
    if (!verifyItem) return null;
    const uid = getCurrentUserId();
    const myVerdicts = {};
    (verifyItem.verifications || []).forEach((v) => {
      if (v.user?.id === uid && v.platform) myVerdicts[v.platform] = v.status;
    });
    return {
      my_verdicts: myVerdicts,
      aggregate: {
        approved: verifyItem.approved ?? 0,
        rejected: verifyItem.rejected ?? 0,
        flagged: verifyItem.flagged ?? 0,
        total: verifyItem.total ?? 0,
      },
    };
  })();

  useEffect(() => {
    if (tab === "records") { fetchRows(rowsPage); }
    else if (isCoverageTab) { fetchPapers(papersPage); }
    else if (tab === "activity") { fetchActivity(activityPage); }
    else if (tab === "tester-stats") { fetchTesterStats(statsPage); }
    else if (tab === "edits") { fetchEdits(editsPage); }
    else if (tab === "videos") { /* the Videos panel loads its own data */ }
    else { fetchItems(itemsPage); }
  }, [
    tab, rowsPage, itemsPage, papersPage, activityPage, editsPage, statsPage,
    isCoverageTab,
    filterType, filterStatus, filterPlatform, filterDateFrom, filterDateTo, minVerifiers, filterTesterUserId, filterQuestionId, filterCoverage, filterVerdict, filterPaperCoverage, filterPaperSubjectId, filterPaperSearch, filterExamCategoryId, filterDifficulty,
    fetchRows, fetchItems, fetchPapers, fetchActivity, fetchTesterStats, fetchEdits,
  ]);

  const typeFilter = (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>Type</InputLabel>
      <Select value={filterType} label="Type" onChange={(e) => { resetAllPages(); setFilterType(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="question">Question</MenuItem>
        <MenuItem value="worksheet">Worksheet</MenuItem>
        <MenuItem value="paper">Paper</MenuItem>
        <MenuItem value="mock_exam">Mock Exam</MenuItem>
      </Select>
    </FormControl>
  );

  const testerFilter = (
    <Autocomplete
      options={testerOptions}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      value={testerOptions.find((o) => String(o.id) === String(filterTesterUserId)) ?? null}
      onChange={(_, selected) => {
        resetAllPages();
        setFilterTesterUserId(selected ? String(selected.id) : "");
      }}
      size="small"
      clearOnEscape
      sx={{ minWidth: 200 }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <span style={{ flex: 1 }}>
            {option.name ?? option}
            {Array.isArray(option.linked_emails) && option.linked_emails.length > 1 && (
              <span style={{ display: "block", fontSize: "0.72rem", color: "#666", marginTop: 2 }}>
                {option.linked_emails.join(" · ")}
              </span>
            )}
          </span>
          {option.total_verifications > 0 && (
            <Chip label={option.total_verifications} size="small" variant="outlined" color="primary" sx={{ ml: 1, fontSize: "0.68rem", height: 20 }} />
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Verified by" placeholder="Anyone" />
      )}
    />
  );

  const platformFilter = (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel>Platform</InputLabel>
      <Select value={filterPlatform} label="Platform" onChange={(e) => { resetAllPages(); setFilterPlatform(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="web">Web</MenuItem>
        <MenuItem value="mobile">Mobile</MenuItem>
      </Select>
    </FormControl>
  );

  const coverageFilter = (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel>Platform Coverage</InputLabel>
      <Select value={filterCoverage} label="Platform Coverage" onChange={(e) => { resetAllPages(); setFilterCoverage(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="both">Both verified</MenuItem>
        <MenuItem value="web_only">Web only</MenuItem>
        <MenuItem value="mobile_only">Mobile only</MenuItem>
        <MenuItem value="none">Unverified</MenuItem>
        <MenuItem value="has_rejected">Has rejected</MenuItem>
      </Select>
    </FormControl>
  );

  const verdictFilter = (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>Verdict</InputLabel>
      <Select value={filterVerdict} label="Verdict" onChange={(e) => { resetAllPages(); setFilterVerdict(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="approved">Accepted</MenuItem>
        <MenuItem value="rejected">Rejected</MenuItem>
        <MenuItem value="mixed">Mixed</MenuItem>
      </Select>
    </FormControl>
  );

  const coverageSummary = {
    total: itemsSummary.total,
    bothVerified: itemsSummary.both_verified,
    webOnly: itemsSummary.web_only,
    mobileOnly: itemsSummary.mobile_only,
    unverified: itemsSummary.unverified,
  };

  const minVerifiersFilter = (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel>Min. Verifiers</InputLabel>
      <Select value={minVerifiers} label="Min. Verifiers" onChange={(e) => { resetAllPages(); setMinVerifiers(e.target.value); }}>
        <MenuItem value="">Any count</MenuItem>
        <MenuItem value="1">1+ verified</MenuItem>
        <MenuItem value="2">2+ verified</MenuItem>
        <MenuItem value="3">3+ verified</MenuItem>
        <MenuItem value="4">4+ verified</MenuItem>
        <MenuItem value="5">5+ verified</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Verification Dashboard</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="By Item" value="items" />
        <Tab label="By Paper" value="papers" />
        <Tab label="By Worksheet" value="worksheets" />
        <Tab label="By Mock Exam" value="mock-exams" />
        <Tab label="Videos" value="videos" />
        <Tab label="Individual Records" value="records" />
        <Tab label="Tester Edits" value="edits" />
        <Tab label="Tester Activity (Today)" value="activity" />
        <Tab label="Tester Stats" value="tester-stats" />
      </Tabs>

      {filterTesterUserId && (tab === "items" || isCoverageTab) && (
        <VerifierSummaryCard
          summary={verifierSummary}
          loading={verifierSummaryLoading}
          error={verifierSummaryError}
          onOpenSection={(sectionTab) => { setPapersPage(1); setFilterPaperCoverage("verifier_any"); setTab(sectionTab); }}
        />
      )}

      {/* ── By Item ──────────────────────────────────────────────────────────── */}
      {tab === "items" && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
            {typeFilter}
            {testerFilter}
            {platformFilter}
            {coverageFilter}
            {verdictFilter}
            {minVerifiersFilter}
            <TextField
              size="small"
              label="Question ID"
              type="number"
              InputLabelProps={{ shrink: true }}
              value={filterQuestionId}
              onChange={(e) => { resetAllPages(); setFilterQuestionId(e.target.value); }}
              sx={{ minWidth: 130 }}
              inputProps={{ min: 1 }}
            />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="From date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateFrom}
              onChange={(e) => { resetAllPages(); setFilterDateFrom(e.target.value); }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateTo}
              onChange={(e) => { resetAllPages(); setFilterDateTo(e.target.value); }}
            />
            {(filterDateFrom || filterDateTo) && (
              <Button size="small" variant="text" onClick={() => { resetAllPages(); setFilterDateFrom(""); setFilterDateTo(""); }} sx={{ textTransform: "none" }}>
                Clear dates
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Chip label={`Total: ${coverageSummary.total}`} size="small" variant="outlined" />
            <Chip label={`Both verified: ${coverageSummary.bothVerified}`} size="small" color="success" variant="outlined" />
            <Chip label={`Web only: ${coverageSummary.webOnly}`} size="small" color="info" variant="outlined" />
            <Chip label={`Mobile only: ${coverageSummary.mobileOnly}`} size="small" color="secondary" variant="outlined" />
            <Chip label={`Unverified: ${coverageSummary.unverified}`} size="small" variant="outlined" />
          </Stack>

          {itemsError && <Alert severity="error" sx={{ mb: 2 }}>{itemsError}</Alert>}

          {itemsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Alert severity="info">No verified items found.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Item ID / Question</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Verdicts</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Platform Coverage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Testers</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Verified</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <ItemAggregateRow
                        key={`${item.verifiable_type}:${item.verifiable_id}`}
                        item={item}
                        onVerify={handleOpenVerify}
                        onView={(id) => handleLoadQuestion(id, "view")}
                        onEdit={(id) => handleLoadQuestion(id, "edit")}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {itemsTotalPages > 1 && (
                <DashboardPagination
                  page={itemsPage}
                  totalPages={itemsTotalPages}
                  total={itemsTotal}
                  onChange={(_, pg) => setItemsPage(pg)}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ── By Paper / By Worksheet / By Mock Exam ───────────────────────────── */}
      {isCoverageTab && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            {testerFilter}
            {platformFilter}
            {isMockExamTab ? (
              <Autocomplete
                freeSolo
                options={examOptions}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                value={examOptions.find((o) => o.name === filterPaperSearch) ?? filterPaperSearch}
                inputValue={filterPaperSearch}
                onInputChange={(_, value, reason) => {
                  if (reason === "reset" && !value) return;
                  setPapersPage(1);
                  setFilterPaperSearch(value);
                }}
                onChange={(_, selected) => {
                  setPapersPage(1);
                  setFilterPaperSearch(typeof selected === "string" ? selected : selected?.name ?? "");
                }}
                size="small"
                sx={{ minWidth: 240 }}
                renderInput={(params) => <TextField {...params} label="Mock exam" />}
              />
            ) : (
              <TextField
                size="small"
                label={isWorksheetTab ? "Search worksheet" : "Search paper"}
                value={filterPaperSearch}
                onChange={(e) => { setPapersPage(1); setFilterPaperSearch(e.target.value); }}
                sx={{ minWidth: 200 }}
              />
            )}
            {!isMockExamTab && !isWorksheetTab && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Shelf</InputLabel>
                <Select
                  value={filterPaperShelf}
                  label="Shelf"
                  onChange={(e) => { setPapersPage(1); setFilterPaperShelf(e.target.value); }}
                >
                  <MenuItem value="">All papers</MenuItem>
                  <MenuItem value="practice">Practice Papers</MenuItem>
                  <MenuItem value="past">Past Papers</MenuItem>
                </Select>
              </FormControl>
            )}
            {isMockExamTab && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterExamCategoryId}
                  label="Category"
                  onChange={(e) => { setPapersPage(1); setFilterExamCategoryId(e.target.value); }}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {examCategoryOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={filterPaperSubjectId}
                label="Subject"
                onChange={(e) => { setPapersPage(1); setFilterPaperSubjectId(e.target.value); }}
              >
                <MenuItem value="">All subjects</MenuItem>
                {subjectOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isWorksheetTab && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={filterDifficulty}
                  label="Difficulty"
                  onChange={(e) => { setPapersPage(1); setFilterDifficulty(e.target.value); }}
                >
                  <MenuItem value="">All levels</MenuItem>
                  <MenuItem value="Easy">Easy</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Hard">Hard</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Coverage</InputLabel>
              <Select
                value={filterPaperCoverage}
                label="Coverage"
                onChange={(e) => { setPapersPage(1); setFilterPaperCoverage(e.target.value); }}
              >
                <MenuItem value="all">{`All ${coverageEntity}s`}</MenuItem>
                <MenuItem value="started">Verification started</MenuItem>
                <MenuItem value="full">Fully verified only</MenuItem>
                <MenuItem value="partial">Partially verified</MenuItem>
                <MenuItem value="approved">{`${coverageEntityTitle} approved (sign-off)`}</MenuItem>
                <MenuItem value="not_approved">{`No ${coverageEntity} sign-off`}</MenuItem>
                <MenuItem value="not_started">Not started</MenuItem>
                {filterTesterUserId && <Divider />}
                {filterTesterUserId && <MenuItem value="verifier_all">{`Every question verified by ${selectedVerifierName}`}</MenuItem>}
                {filterTesterUserId && <MenuItem value="verifier_any">{`Some verified by ${selectedVerifierName}`}</MenuItem>}
                {filterTesterUserId && <MenuItem value="verifier_none">{`None verified by ${selectedVerifierName}`}</MenuItem>}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="From date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateFrom}
              onChange={(e) => { resetAllPages(); setFilterDateFrom(e.target.value); }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateTo}
              onChange={(e) => { resetAllPages(); setFilterDateTo(e.target.value); }}
            />
            {(filterDateFrom || filterDateTo) && (
              <Button size="small" variant="text" onClick={() => { resetAllPages(); setFilterDateFrom(""); setFilterDateTo(""); }} sx={{ textTransform: "none" }}>
                Clear dates
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={() => fetchPapers(papersPage)} disabled={papersLoading} sx={{ textTransform: "none" }}>
              Refresh
            </Button>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap" }}>
            {[
              { key: "all", label: `${coverageEntityTitle}s: ${papersSummary.total}`, color: "default" },
              { key: "full", label: `Fully verified: ${papersSummary.fully_verified}`, color: "success" },
              { key: "partial", label: `Partial: ${papersSummary.partially_verified}`, color: "warning" },
              { key: "not_started", label: `Not started: ${papersSummary.not_started ?? 0}`, color: "default" },
              { key: "approved", label: `${coverageEntityTitle} sign-off: ${papersSummary.paper_approved ?? 0}`, color: "success" },
              ...(papersSummary.verifier ? [
                { key: "verifier_all", label: `All verified by ${papersSummary.verifier.user?.name}: ${papersSummary.verifier.items_all_verified}`, color: "primary" },
                { key: "verifier_any", label: `Some by ${papersSummary.verifier.user?.name}: ${papersSummary.verifier.items_with_verifier}`, color: "primary" },
                { key: "verifier_none", label: `None by ${papersSummary.verifier.user?.name}: ${papersSummary.verifier.items_none_verified}`, color: "default" },
              ] : []),
            ].map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                color={c.color}
                variant={filterPaperCoverage === c.key ? "filled" : "outlined"}
                onClick={() => { setPapersPage(1); setFilterPaperCoverage(c.key); }}
                sx={{ cursor: "pointer" }}
              />
            ))}
          </Stack>

          <PaperCoveragePanel
            papers={papers}
            loading={papersLoading}
            error={papersError}
            entityLabel={coverageEntity}
            verifierName={selectedVerifierName}
            onViewPaper={isWorksheetTab ? undefined : (paperId, row) => {
              const qbFilter = isMockExamTab
                ? { filterMockExamName: row?.paper_name ?? "", filterPaperId: "" }
                : { filterPaperId: String(paperId), filterMockExamName: "" };
              // Carry the "Verified by" choice over so the Question Bank shows that verifier's questions.
              if (filterTesterUserId) {
                qbFilter.filterTesterUserId = String(filterTesterUserId);
                qbFilter.filterVerifierPlatform = filterPlatform || "";
              }
              try {
                const existing = JSON.parse(sessionStorage.getItem(QB_FILTER_KEY) || "{}");
                sessionStorage.setItem(QB_FILTER_KEY, JSON.stringify({ ...existing, ...qbFilter, page: 0 }));
              } catch {}
              navigate("/admin/question-bank");
            }}
            onOpenQuestion={openQuestionForVerification}
            openingQuestion={openingQuestionId}
          />
          <DashboardPagination
            page={papersPage}
            totalPages={papersTotalPages}
            total={papersTotal}
            onChange={(_, pg) => setPapersPage(pg)}
          />
        </>
      )}

      {/* ── Individual Records ───────────────────────────────────────────────── */}
      {tab === "records" && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
            {typeFilter}
            {testerFilter}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => { resetAllPages(); setFilterStatus(e.target.value); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            {platformFilter}
            <TextField
              size="small"
              label="From date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateFrom}
              onChange={(e) => { resetAllPages(); setFilterDateFrom(e.target.value); }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateTo}
              onChange={(e) => { resetAllPages(); setFilterDateTo(e.target.value); }}
            />
            {(filterDateFrom || filterDateTo) && (
              <Button size="small" variant="text" onClick={() => { resetAllPages(); setFilterDateFrom(""); setFilterDateTo(""); }} sx={{ textTransform: "none" }}>
                Clear dates
              </Button>
            )}
            {minVerifiersFilter}
          </Stack>

          {rowsError && <Alert severity="error" sx={{ mb: 2 }}>{rowsError}</Alert>}

          {rowsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Alert severity="info">No verifications found for the selected filters.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Item ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tester</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Verdict</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Comments</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <VerificationRow key={row.id} row={row} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {rowsTotalPages > 1 && (
                <DashboardPagination
                  page={rowsPage}
                  totalPages={rowsTotalPages}
                  total={rowsTotal}
                  onChange={(_, pg) => setRowsPage(pg)}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ── Explanation videos ───────────────────────────────────────────────── */}
      {tab === "videos" && <VideoVerificationPanel />}

      {/* ── Tester Activity ──────────────────────────────────────────────────── */}
      {tab === "activity" && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", alignItems: "center" }}>
            {typeFilter}
            {testerFilter}
            {platformFilter}
            <Button size="small" variant="outlined" onClick={() => fetchActivity(activityPage)} disabled={activityLoading} sx={{ textTransform: "none" }}>
              Refresh
            </Button>
            <Typography variant="caption" color="text.secondary">
              Showing verifications from {filterDateFrom || "—"} to {filterDateTo || "—"}
            </Typography>
          </Stack>
          <TesterActivityPanel rows={activityRows} loading={activityLoading} error={activityError} />
          <DashboardPagination
            page={activityPage}
            totalPages={activityTotalPages}
            total={activityTotal}
            onChange={(_, pg) => setActivityPage(pg)}
          />
        </>
      )}

      {/* ── Tester Stats ─────────────────────────────────────────────────────── */}
      {tab === "tester-stats" && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", alignItems: "center" }}>
            {typeFilter}
            {platformFilter}
            <TextField
              size="small"
              label="From date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateFrom}
              onChange={(e) => { resetAllPages(); setFilterDateFrom(e.target.value); }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateTo}
              onChange={(e) => { resetAllPages(); setFilterDateTo(e.target.value); }}
            />
            {(filterDateFrom || filterDateTo) && (
              <Button size="small" variant="text" onClick={() => { resetAllPages(); setFilterDateFrom(""); setFilterDateTo(""); }} sx={{ textTransform: "none" }}>
                Clear dates
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={() => fetchTesterStats(statsPage)} disabled={testerStatsLoading} sx={{ textTransform: "none" }}>
              Refresh
            </Button>
          </Stack>
          <TesterStatsPanel
            stats={testerStats}
            loading={testerStatsLoading}
            error={testerStatsError}
            onShowItems={(userId) => {
              resetAllPages();
              setFilterTesterUserId(String(userId));
              setTab("items");
            }}
          />
          <DashboardPagination
            page={statsPage}
            totalPages={statsTotalPages}
            total={statsTotal}
            onChange={(_, pg) => setStatsPage(pg)}
          />
        </>
      )}

      {/* ── Tester Edits ─────────────────────────────────────────────────────── */}
      {tab === "edits" && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", alignItems: "center" }}>
            {testerFilter}
            {platformFilter}
            <TextField
              size="small"
              label="Question ID"
              type="number"
              InputLabelProps={{ shrink: true }}
              value={filterQuestionId}
              onChange={(e) => { resetAllPages(); setFilterQuestionId(e.target.value); }}
              sx={{ minWidth: 130 }}
              inputProps={{ min: 1 }}
            />
            <TextField
              size="small"
              label="From date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateFrom}
              onChange={(e) => { resetAllPages(); setFilterDateFrom(e.target.value); }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDateTo}
              onChange={(e) => { resetAllPages(); setFilterDateTo(e.target.value); }}
            />
            {(filterDateFrom || filterDateTo) && (
              <Button size="small" variant="text" onClick={() => { resetAllPages(); setFilterDateFrom(""); setFilterDateTo(""); }} sx={{ textTransform: "none" }}>
                Clear dates
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={() => fetchEdits(editsPage)} disabled={editsLoading} sx={{ textTransform: "none" }}>
              Refresh
            </Button>
            <Typography variant="caption" color="text.secondary">
              Detailed before→after of every question/answer/explanation change.
            </Typography>
          </Stack>
          <TesterEditsPanel edits={edits} loading={editsLoading} error={editsError} />
          <DashboardPagination
            page={editsPage}
            totalPages={editsTotalPages}
            total={editsTotal}
            onChange={(_, pg) => setEditsPage(pg)}
          />
        </>
      )}

      {/* ── Question view dialog ─────────────────────────────────────────────── */}
      <Dialog open={viewQDialogOpen} onClose={() => { setViewQDialogOpen(false); setViewQVerifications([]); }} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Question Details</Typography>
            {viewQData && <Typography variant="body2" color="text.secondary">ID: #{viewQData.id}</Typography>}
          </Box>
          <IconButton onClick={() => { setViewQDialogOpen(false); setViewQVerifications([]); }} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewQLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : viewQData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Chip label={`#${viewQData.id}`} size="small" sx={{ fontWeight: 700, bgcolor: "#f3e5f5", color: "#7b1fa2" }} />
                <Chip label={viewQData.type} size="small" variant="outlined" />
                {viewQData.difficulty && <Chip label={viewQData.difficulty} size="small" color="info" variant="outlined" />}
                {viewQData.default_marks != null && (
                  <Chip label={`${viewQData.default_marks} mark${viewQData.default_marks !== 1 ? "s" : ""}`} size="small" variant="outlined" />
                )}
                {viewQData.category_name && <Chip label={viewQData.category_name} size="small" color="default" variant="outlined" />}
              </Box>
              <Divider />
              {viewQData.question_text && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Question</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: "#fafafa" }}>
                    <HtmlWithLatex html={viewQData.question_text} />
                  </Paper>
                </Box>
              )}
              {viewQData.image_url && (
                <Box component="img" src={viewQData.image_url} alt="" sx={{ maxWidth: "100%", maxHeight: 240, borderRadius: 1, border: "1px solid", borderColor: "divider", objectFit: "contain" }} />
              )}
              {["multiple-choice", "multiple-select", "true-false"].includes(viewQData.type) && viewQData.options?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Options</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {viewQData.options.map((opt, i) => (
                      <Box key={opt.id ?? i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1, border: "1px solid", borderColor: opt.is_correct ? "success.main" : "#e0e0e0", bgcolor: opt.is_correct ? "rgba(46,125,50,0.05)" : "transparent" }}>
                        {opt.is_correct
                          ? <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 16, flexShrink: 0 }} />
                          : <CancelOutlinedIcon sx={{ color: "#bdbdbd", fontSize: 16, flexShrink: 0 }} />}
                        <Typography variant="body2" sx={{ fontWeight: opt.is_correct ? 600 : 400 }}>{opt.option_text || opt.text || ""}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {viewQData.answer?.correct_answer_text && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Answer</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: "rgba(46,125,50,0.05)", borderColor: "success.light" }}>
                    <Box sx={{ "& p": { m: 0 } }}>
                      <HtmlWithLatex html={String(viewQData.answer.correct_answer_text)} />
                    </Box>
                  </Paper>
                </Box>
              )}
              {viewQData.answer?.correct_blanks?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Blank Answers</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {viewQData.answer.correct_blanks.map((b, i) => (
                      <Chip key={i} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              {viewQData.type === "grouped" && viewQData.sub_questions?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                    Sub-questions ({viewQData.sub_questions.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {viewQData.sub_questions.map((sq, idx) => (
                      <Paper key={sq.id ?? idx} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#7b1fa2", display: "block", mb: 0.5 }}>
                          Part {idx + 1} — {sq.type}
                        </Typography>
                        {sq.question_text && <HtmlWithLatex html={sq.question_text} />}
                        {sq.answer?.correct_answer_text && (
                          <Box sx={{ mt: 0.5, "& p": { m: 0 } }}>
                            <Typography component="span" variant="body2" sx={{ color: "success.dark", fontWeight: 600 }}>Answer: </Typography>
                            <HtmlWithLatex html={sq.answer.correct_answer_text} />
                          </Box>
                        )}
                        {sq.answer?.correct_blanks?.length > 0 && (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                            {sq.answer.correct_blanks.map((b, bi) => (
                              <Chip key={bi} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" />
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
              {viewQData.explanation && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Explanation</Typography>
                  <HtmlWithLatex html={viewQData.explanation} />
                </Box>
              )}
              {Array.isArray(viewQData.topics) && viewQData.topics.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>Topics</Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    {viewQData.topics.map((t) => (
                      <Chip key={t.id ?? t.name} label={t.name} size="small" variant="outlined" sx={{ fontSize: "0.72rem" }} />
                    ))}
                  </Stack>
                </Box>
              )}
              {Array.isArray(viewQData.linked_papers) && viewQData.linked_papers.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
                    Linked Papers ({viewQData.linked_papers.length})
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    {viewQData.linked_papers.map((p) => (
                      <Chip key={p.id ?? p.title} label={p.title ?? p.name ?? `#${p.id}`} size="small" color="warning" variant="outlined" sx={{ fontSize: "0.72rem" }} />
                    ))}
                  </Stack>
                </Box>
              )}
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>Verified By</Typography>
                {viewQVerificationsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading…</Typography>
                  </Box>
                ) : viewQVerifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Not yet verified by any tester.</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {viewQVerifications.map((v) => {
                      const cfg = v.status === "approved"
                        ? { label: "Approved", color: "success" }
                        : v.status === "rejected"
                          ? { label: "Rejected", color: "error" }
                          : { label: v.status, color: "default" };
                      return (
                        <Box key={v.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1.5, border: "1px solid #e0e0e0", bgcolor: "#fafafa" }}>
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{v.user?.name ?? `User #${v.user_id}`}</Typography>
                          <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {v.verified_at ? new Date(v.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">No data available.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          {viewQData && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setViewQDialogOpen(false);
                setViewQVerifications([]);
                setEditQData(viewQData);
                setEditQOpen(true);
              }}
              sx={{ textTransform: "none" }}
            >
              Edit Question
            </Button>
          )}
          <Button onClick={() => { setViewQDialogOpen(false); setViewQVerifications([]); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Accept / reject dialog ───────────────────────────────────────────── */}
      <QuestionVerifyDialog
        open={verifyOpen}
        question={verifyQuestion}
        itemData={verifyItemData}
        loading={verifyLoading}
        submitting={verifySubmitting}
        onClose={() => { setVerifyOpen(false); setVerifyItem(null); setVerifyQuestion(null); }}
        onSubmit={handleSubmitVerify}
        onEdit={(q) => {
          setVerifyOpen(false);
          setEditQData(q);
          setEditQOpen(true);
        }}
      />

      {/* ── Question edit dialog ─────────────────────────────────────────────── */}
      {editQOpen && editQData && (
        <QuestionBankQuestionEditDialog
          question={editQData}
          saving={editQSaving}
          onClose={() => { setEditQOpen(false); setEditQData(null); }}
          onSave={async (payload) => {
            setEditQSaving(true);
            try {
              await api.put(`/admin/question-bank/${editQData.id}`, payload);
              toast.success("Question saved.");
              setEditQOpen(false);
              setEditQData(null);
              if (tab === "items") fetchItems(itemsPage);
            } catch (err) {
              const errs = err?.response?.data?.errors;
              const first = errs ? Object.values(errs)[0]?.[0] : null;
              toast.error(first || err?.response?.data?.message || "Failed to save question.");
            } finally {
              setEditQSaving(false);
            }
          }}
        />
      )}
    </Box>
  );
}
